import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { errorMessage } from "@/lib/api";
import { AuthShell, AuthInput, PrimaryButton } from "@/components/auth/AuthShell";

export default function LoginPage() {
  const { login, user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next") && params.get("next").startsWith("/") ? params.get("next") : "/dashboard";
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  React.useEffect(() => {
    if (!authLoading && user) navigate(next, { replace: true });
  }, [authLoading, user, navigate, next]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(identifier.trim(), password);
      navigate(next, { replace: true });
    } catch (err) {
      setError(errorMessage(err, "Unable to sign in"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      title="Sign in to the dashboard"
      subtitle="For EMERGE team members. Clients do not need an account."
      footer={
        <p>
          Looking for the intake form?{" "}
          <Link to="/intake" className="font-semibold text-primary hover:underline" data-testid="login-intake-link">
            Open the client form
          </Link>
        </p>
      }
    >
      <form onSubmit={onSubmit} className="space-y-5" data-testid="login-form">
        <AuthInput label="Username or email" id="identifier" data-testid="login-identifier-input" value={identifier} onChange={(e) => setIdentifier(e.target.value)} autoComplete="username" required autoFocus />
        <AuthInput label="Password" id="password" type="password" data-testid="login-password-input" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" required />
        {error && (
          <div className="rounded-[10px] border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive" role="alert" data-testid="login-error">
            {error}
          </div>
        )}
        <PrimaryButton loading={loading} data-testid="login-submit-button">
          {loading && <Loader2 className="h-4 w-4 animate-spin" />}
          Sign in
        </PrimaryButton>
        <div className="text-center text-sm">
          <Link to="/forgot-password" className="font-medium text-primary hover:underline" data-testid="login-forgot-link">
            Forgot your password?
          </Link>
        </div>
      </form>
    </AuthShell>
  );
}
